import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import clsx from 'clsx';

/** Matches the panel's `w-64`; needed in JS to keep it inside the viewport. */
const PANEL_WIDTH = 256;
/** Breathing room between the trigger and the panel, and against the viewport edge. */
const GAP = 8;

/**
 * A small "i" that explains what a figure actually counts.
 *
 * Opens on hover *and* on click. Hover alone would be unreachable on touch
 * devices; click alone makes the reader work for something they should get by
 * pointing at it. Keyboard focus opens it too, for the same reason.
 *
 * A click pins the panel open so it survives the pointer leaving — otherwise
 * moving toward the text to read it would dismiss it.
 *
 * The panel renders through a portal rather than as a positioned child.
 * `.card` carries `backdrop-blur`, which creates a stacking context, so an
 * absolutely-positioned panel was trapped inside its own card and painted
 * *behind* every later card — the explanation appeared blurred and cut off
 * exactly where it crossed the next card's edge. No z-index can escape that;
 * leaving the tree is the only fix.
 *
 * The panel is `role="tooltip"` and wired to the trigger with
 * `aria-describedby`, so a screen reader announces the explanation along with
 * the label rather than reading a bare "i" button.
 */
export function InfoButton({
  label,
  children,
  align = 'left',
  className,
}: {
  /** What the "i" is explaining. Used for the button's accessible name. */
  label: string;
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;
  const id = useId();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Fixed positioning is relative to the viewport, so the panel has to be
  // re-placed whenever anything moves underneath it. `true` on the scroll
  // listener catches scrolling containers, not just the window.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const preferred = align === 'right' ? rect.right - PANEL_WIDTH : rect.left;
      const maxLeft = Math.max(GAP, window.innerWidth - PANEL_WIDTH - GAP);
      setPosition({ top: rect.bottom + GAP, left: Math.min(Math.max(GAP, preferred), maxLeft) });
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, align]);

  // Dismissal has to account for the panel living outside this component's
  // subtree, so a click on the explanation itself does not close it.
  useEffect(() => {
    if (!pinned) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setPinned(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPinned(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [pinned]);

  return (
    <span
      className={clsx('relative inline-flex', className)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setPinned((v) => !v)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label={`What does “${label}” mean?`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        className={clsx(
          'inline-flex h-4 w-4 items-center justify-center rounded-full transition',
          open ? 'text-brand-300' : 'text-slate-600 hover:text-slate-400',
        )}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>

      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role="tooltip"
              style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
              /**
               * `normal-case` and `tracking-normal` are not decoration. These
               * triggers sit inside uppercase, wide-tracked stat labels, and
               * the panel inherited both — rendering every explanation as
               * shouted, letter-spaced capitals.
               */
              className={clsx(
                'fixed z-[100] animate-fade-in rounded-lg border border-surface-border bg-surface-overlay',
                'px-3 py-2.5 text-xs font-normal normal-case leading-relaxed tracking-normal',
                'text-slate-300 shadow-2xl',
              )}
            >
              <span className="mb-1 block font-medium text-slate-100">{label}</span>
              {children}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
